import React, { useCallback, useMemo } from "react"

import Slider from "rc-slider"
import "rc-slider/assets/index.css"

import styles from "./EncouragingRereservationUnitsSlider.module.scss"

// 来店回数区間 [rangeStart, rangeEnd) を unit に分割するスライダー。
// 再予約案内区間 (encouragingRereservationUnits) と手動請求区間 (manuallyClaimingUnits) の両方で使う。
// boundaries = 2 番目以降の unit の開始来店回数 (先頭 unit の開始は rangeStart に固定)。
// 各 boundary は (rangeStart, rangeEnd) の開区間に収まり、互いに 1 以上離れる (= unit の開始来店回数が重複しない)。
type EncouragingRereservationUnitsSliderProps = {
	rangeStart: number
	rangeEnd: number
	boundaries: number[]
	onBoundariesChange: (boundaries: number[]) => void
	// unit の色 (省略時は再予約案内の配色)
	getSegmentColor?: (index: number) => string
}

// 20 はバックエンド側で 9999 (上限なし) に変換される
const UNLIMITED_VISIT_COUNT = 20

const UNIT_SEGMENT_COLORS = [
	"#06D6A0",
	"#118AB2",
	"#8338EC",
	"#EF476F",
	"#FFD166",
	"#FB5607",
	"#3A86FF",
	"#FF006E",
]

export const getUnitSegmentColor = (index: number): string => {
	return UNIT_SEGMENT_COLORS[index % UNIT_SEGMENT_COLORS.length]
}

// 手動請求 unit の配色。再予約案内 unit と見分けられるよう、手動請求の黄色 (#FFD166) から区間ごとに緑を足していく
const MANUALLY_CLAIMING_UNIT_SEGMENT_COLORS = [
	"#FFD166",
	"#E8D25C",
	"#CDD155",
	"#AECB4F",
	"#8EC34A",
	"#6DB848",
	"#4FA947",
	"#379646",
]

export const getManuallyClaimingUnitSegmentColor = (index: number): string => {
	return MANUALLY_CLAIMING_UNIT_SEGMENT_COLORS[
		index % MANUALLY_CLAIMING_UNIT_SEGMENT_COLORS.length
	]
}

export type UnitSegment = {
	index: number
	start: number
	end: number
}

// unit の開始来店回数 (昇順) と再予約案内区間の終端から、各 unit の [start, end) を組み立てる
export const convertUnitMinVisitCountsToSegments = (
	minVisitCounts: number[],
	rangeEnd: number
): UnitSegment[] => {
	return minVisitCounts.map((start, index) => {
		let end: number
		if (index + 1 < minVisitCounts.length) {
			end = minVisitCounts[index + 1]
		} else {
			end = rangeEnd
		}
		return { index, start, end }
	})
}

// FunctionVisitCountsSlider と同じ表記 (20 → 9999)
export const formatUnitVisitCounts = (segment: UnitSegment): string => {
	if (segment.start >= segment.end) {
		return "無効"
	}
	if (segment.end >= UNLIMITED_VISIT_COUNT) {
		return `${segment.start} 回以上`
	}
	const until = segment.end - 1
	if (segment.start === until) {
		return `${segment.start} 回`
	}
	return `${segment.start} ~ ${until} 回`
}

// 以下は unit の配列を扱う純粋関数。再予約案内 unit・手動請求 unit の両方の編集 UI から使う

// 区間 [rangeStart, rangeEnd) の変更に unit を追従させた結果を返す:
// - 先頭 unit の開始は区間開始に固定 (区間に穴を作らない)
// - 区間外に出た unit / 順序が崩れた unit は落とす
// - 区間が無くなったら unit を全削除
export const followUnitsToRange = <T extends { minVisitCount: number }>(
	units: T[],
	rangeStart: number,
	rangeEnd: number
): T[] => {
	const next: T[] = []
	if (rangeStart >= rangeEnd) return next
	units.forEach((unit, unitIndex) => {
		if (unitIndex === 0) {
			next.push({ ...unit, minVisitCount: rangeStart })
			return
		}
		const previousMinVisitCount = next[next.length - 1].minVisitCount
		if (
			unit.minVisitCount > previousMinVisitCount &&
			unit.minVisitCount < rangeEnd
		) {
			next.push(unit)
		}
	})
	return next
}

// 数値入力の途中で一時的に順序が崩れてもスライダーが壊れないよう、表示用の boundaries を昇順・範囲内に丸める
export const toDisplayUnitBoundaries = (
	minVisitCounts: number[],
	rangeStart: number,
	rangeEnd: number
): number[] => {
	const boundaries: number[] = []
	minVisitCounts.slice(1).forEach((minVisitCount, boundaryIndex) => {
		let previous: number
		if (boundaryIndex === 0) {
			previous = rangeStart
		} else {
			previous = boundaries[boundaryIndex - 1]
		}
		const lowerLimit = previous + 1
		const upperLimit =
			rangeEnd - 1 - (minVisitCounts.length - 2 - boundaryIndex)
		boundaries.push(Math.min(Math.max(minVisitCount, lowerLimit), upperLimit))
	})
	return boundaries
}

// unit の開始来店回数として入力できる範囲 (前の unit + 1 〜 次の unit - 1)。先頭 unit (index 0) には使わない
export const getUnitMinVisitCountLimits = (
	minVisitCounts: number[],
	unitIndex: number,
	rangeEnd: number
): { min: number; max: number } => {
	const min = minVisitCounts[unitIndex - 1] + 1
	let nextStart: number
	if (unitIndex + 1 < minVisitCounts.length) {
		nextStart = minVisitCounts[unitIndex + 1]
	} else {
		nextStart = rangeEnd
	}
	return { min, max: nextStart - 1 }
}

// 「＋区間を追加」で割る区間: 幅が 2 以上ある一番広い区間 (割れる区間が無ければ null)
export const findSplittableSegment = (
	segments: UnitSegment[]
): UnitSegment | null => {
	let widest: UnitSegment | null = null
	for (const segment of segments) {
		if (segment.end - segment.start < 2) continue
		if (
			widest === null ||
			segment.end - segment.start > widest.end - widest.start
		) {
			widest = segment
		}
	}
	return widest
}

const formatMark = (visitCount: number): string => {
	if (visitCount >= UNLIMITED_VISIT_COUNT) {
		return "9999"
	}
	return visitCount.toString()
}

export const EncouragingRereservationUnitsSlider: React.FC<
	EncouragingRereservationUnitsSliderProps
> = ({
	rangeStart,
	rangeEnd,
	boundaries,
	onBoundariesChange,
	getSegmentColor = getUnitSegmentColor,
}) => {
	const totalRange = Math.max(rangeEnd - rangeStart, 1)

	const marks = useMemo(() => {
		const markObj: Record<number, string> = {}
		let step: number
		if (rangeEnd - rangeStart > 10) {
			step = 2
		} else {
			step = 1
		}
		for (let i = rangeStart; i < rangeEnd; i += step) {
			markObj[i] = formatMark(i)
		}
		markObj[rangeEnd] = formatMark(rangeEnd)
		return markObj
	}, [rangeStart, rangeEnd])

	const trackStyles = useMemo(() => {
		const positions = [rangeStart, ...boundaries, rangeEnd]
		const tracks: React.CSSProperties[] = []
		for (let i = 0; i < positions.length - 1; i++) {
			const left = ((positions[i] - rangeStart) / totalRange) * 100
			const width = ((positions[i + 1] - positions[i]) / totalRange) * 100
			tracks.push({
				left: `${left}%`,
				width: `${width}%`,
				backgroundColor: getSegmentColor(i),
			})
		}
		return tracks
	}, [rangeStart, rangeEnd, boundaries, totalRange, getSegmentColor])

	// 端のハンドルが rangeStart / rangeEnd に張り付くと開始来店回数が重複・範囲外になるため開区間に押し戻す
	const handleChange = useCallback(
		(values: number[]) => {
			const sorted = [...values].sort((a, b) => a - b)
			const clamped = sorted.map((value, index) => {
				const lowerLimit = rangeStart + 1 + index
				const upperLimit = rangeEnd - 1 - (sorted.length - 1 - index)
				return Math.min(Math.max(value, lowerLimit), upperLimit)
			})
			onBoundariesChange(clamped)
		},
		[rangeStart, rangeEnd, onBoundariesChange]
	)

	return (
		<div className={styles.sliderWrapper}>
			<div className={styles.customTrack}>
				{trackStyles.map((style, index) => (
					<div key={index} className={styles.trackSegment} style={style} />
				))}
			</div>
			{boundaries.length > 0 ? (
				<Slider
					range
					min={rangeStart}
					max={rangeEnd}
					step={1}
					value={boundaries}
					onChange={(value) => {
						if (Array.isArray(value)) {
							handleChange(value)
						}
					}}
					pushable={1}
					allowCross={false}
					marks={marks}
				/>
			) : (
				// 区間が 1 つだけのときはハンドルなし (目盛りだけ表示)
				<Slider
					min={rangeStart}
					max={rangeEnd}
					step={1}
					value={rangeStart}
					marks={marks}
					disabled
					className={styles.noHandleSlider}
				/>
			)}
		</div>
	)
}
